/**
 * i18n CDN Lazy Loading Plugin (SSR + Client)
 *
 * CDN'den çevirileri lazy load eder.
 * Bundle'a çeviri dahil edilmez, runtime'da yüklenir.
 * Fallback: tr (varsayılan)
 *
 * CDN yapısı:
 * - {assetBaseUrl}/destek/locale/tr.json
 * - {assetBaseUrl}/destek/locale/en.json
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()
  const assetBaseUrl = config.public.assetBaseUrl as string
  const cdnBasePath = `${assetBaseUrl}/destek/locale`

  // i18n instance'ını al
  const i18n = nuxtApp.$i18n as any

  // Yüklenen dilleri takip et
  const loadedLanguages = new Set<string>()

  /**
   * CDN'den çevirileri yükle
   */
  async function loadLocaleMessages(locale: string): Promise<boolean> {
    const normalizedLocale = locale.startsWith('en') ? 'en' : 'tr'

    // Zaten yüklenmişse atla (normalizedLocale ile kontrol)
    if (loadedLanguages.has(normalizedLocale)) {
      return true
    }

    // SSR'dan gelen mesajlar varsa tekrar fetch etme (client hydration optimizasyonu)
    const existingMessages = i18n.messages.value[normalizedLocale]
    if (existingMessages && Object.keys(existingMessages).length > 0) {
      loadedLanguages.add(normalizedLocale)
      return true
    }

    try {
      const url = `${cdnBasePath}/${normalizedLocale}.json`

      // $fetch hem server hem client'da çalışır
      const messages = await $fetch<Record<string, any>>(url)

      // i18n'e mesajları ekle
      i18n.setLocaleMessage(normalizedLocale, messages)

      // BCP-47 varyantlarını da ekle
      if (normalizedLocale === 'tr') {
        i18n.setLocaleMessage('tr-TR', messages)
      } else {
        i18n.setLocaleMessage('en-US', messages)
      }

      // Dev: merge local locale JSON over CDN so keys added during
      // development show up before the CDN bundle is republished.
      if (import.meta.dev) {
        try {
          const local = (await import(`~/locales/${normalizedLocale}.json`)).default
          i18n.mergeLocaleMessage(normalizedLocale, local)
          if (normalizedLocale === 'tr') i18n.mergeLocaleMessage('tr-TR', local)
          else i18n.mergeLocaleMessage('en-US', local)
        } catch {
          /* dev override missing — non-fatal */
        }
      }

      loadedLanguages.add(normalizedLocale)

      return true
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Failed to load ${normalizedLocale} from CDN:`, err.message)

      // Fallback: tr dili dene (eğer en istenmişse)
      if (normalizedLocale === 'en' && !loadedLanguages.has('tr')) {
        // eslint-disable-next-line no-console
        console.warn('[i18n] Falling back to Turkish translations')
        return loadLocaleMessages('tr')
      }

      return false
    }
  }

  const localeCookie = useCookie('locale')
  const initialLocale = localeCookie.value === 'en' ? 'en' : 'tr'
  i18n.locale.value = initialLocale
  localeCookie.value = initialLocale

  await loadLocaleMessages(initialLocale)

  /**
   * Uygulama dilini değiştirir (çeviriler + cookie + i18n locale).
   * Cookie yazımı plugin'in kendi ref'i üzerinden yapılır; SSR'da aynı isim
   * için ikinci bir useCookie ref'i oluşturmak response flush'ta çakışma
   * yaratıyor (plugin'in ilk yazdığı değer kazanıyor) — bu yüzden dil
   * değiştiren herkes bu fonksiyonu kullanmalı.
   */
  async function setAppLocale(locale: string): Promise<void> {
    const normalized = (locale || '').startsWith('en') ? 'en' : 'tr'
    await loadLocaleMessages(normalized)
    localeCookie.value = normalized
    i18n.locale.value = normalized
  }

  // Client-side'da dil değişikliğini dinle
  if (import.meta.client) {
    watch(
      () => i18n.locale.value,
      async (newLocale: string) => {
        await loadLocaleMessages(newLocale)
      },
    )
  }

  // Plugin API'si
  return {
    provide: {
      loadLocaleMessages,
      loadedLanguages,
      setAppLocale,
    },
  }
})
